import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

export const PST_TIMEZONE = 'America/Los_Angeles';

// Strings with an explicit UTC offset (e.g. from Prismic: "2026-05-11T21:00:00+0000")
// must be parsed first so the offset is respected before converting to PST.
// Timezone-naive strings (e.g. "2023-05-01 08:30") are treated as PST directly.
const hasUtcOffset = (dateStr: string) =>
  /Z$|[+-]\d{2}:?\d{2}$/.test(dateStr.trim());

export const parseDateInPST = (dateStr: string, tz: string = PST_TIMEZONE) =>
  hasUtcOffset(dateStr) ? dayjs(dateStr).tz(tz) : dayjs.tz(dateStr, tz);

type RangeBounds = { start: number; end: number } | null;

// Timezone-aware parsing is expensive and callers (e.g. PDP deals banners)
// re-check the same Prismic date strings on every render, so each distinct
// range is parsed once into epoch bounds; only "now" is read per call.
const rangeBoundsCache = new Map<string, RangeBounds>();

const parseRangeBounds = (startDate: string, endDate: string): RangeBounds => {
  try {
    const start = parseDateInPST(startDate, PST_TIMEZONE);
    let end = parseDateInPST(endDate, PST_TIMEZONE);

    // If endDate has no time (defaults to midnight), adjust to end of day
    if (end.isSame(end.startOf('day'))) {
      end = end.endOf('day');
    }

    return { start: start.valueOf(), end: end.valueOf() };
  } catch (error) {
    console.error('Error checking date range:', error);
    return null;
  }
};

export const isWithinDateRangePST = (
  startDate = '0001-01-01',
  endDate = '9999-12-31'
) => {
  if (process.env.NEXT_PUBLIC_NYD_DATE_OVERRIDE === 'true') {
    return true;
  }

  const key = `${startDate}|${endDate}`;
  let bounds = rangeBoundsCache.get(key);
  if (bounds === undefined) {
    bounds = parseRangeBounds(startDate, endDate);
    rangeBoundsCache.set(key, bounds);
  }
  if (!bounds) return false;

  const now = Date.now();
  return bounds.start <= now && now <= bounds.end;
};
